// Pure helpers for vendor ratings (Nov 2026). No Firebase imports.
//
// Only an owner whose plan accepted a vendor's bid may rate that vendor
// (Jason, Nov 2 2026). A bid carries the vendor's own words, not a directory
// id, so the owner links an accepted bid to a directory vendor once, when
// they first rate it; rules check that link.

import { vendorNameKey } from './vendors';
import type { PlanBid, Vendor, VendorRating } from '../types';

export interface RatingSummary {
  /** Mean stars, rounded to one decimal. */
  average: number;
  count: number;
}

/** Average and count per vendor, hidden ratings left out. */
export function summarizeRatings(ratings: VendorRating[]): Map<string, RatingSummary> {
  const totals = new Map<string, { sum: number; count: number }>();
  for (const r of ratings) {
    if (r.hidden) continue;
    const t = totals.get(r.vendorId) ?? { sum: 0, count: 0 };
    t.sum += r.stars;
    t.count += 1;
    totals.set(r.vendorId, t);
  }
  const out = new Map<string, RatingSummary>();
  for (const [vendorId, t] of totals) {
    out.set(vendorId, { average: Math.round((t.sum / t.count) * 10) / 10, count: t.count });
  }
  return out;
}

/** "4.5 stars from 2 ratings". */
export function ratingSummaryText(summary: RatingSummary): string {
  const ratings = summary.count === 1 ? '1 rating' : `${summary.count} ratings`;
  return `${summary.average.toFixed(1)} stars from ${ratings}`;
}

function emailDomain(email: string): string | undefined {
  const at = email.lastIndexOf('@');
  return at >= 0
    ? email
        .slice(at + 1)
        .trim()
        .toLowerCase() || undefined
    : undefined;
}

/**
 * The owner's accepted bids that could back a rating of `vendor`, best first:
 * a bid already linked to this vendor, then unlinked bids that look like
 * theirs (same name without Inc or LLC, or an email at the vendor's website
 * domain), then unlinked bids on a bundle where the owner shortlisted this
 * vendor. A bid linked to another vendor never counts: the link is set once.
 */
export function ratableBids(
  vendor: Pick<Vendor, 'id' | 'nameKey' | 'domain'>,
  bids: PlanBid[],
  shortlistedBundleIds: ReadonlySet<string>
): PlanBid[] {
  const linked: PlanBid[] = [];
  const named: PlanBid[] = [];
  const shortlisted: PlanBid[] = [];
  for (const bid of bids) {
    if (bid.status !== 'accepted') continue;
    if (bid.vendorId) {
      if (bid.vendorId === vendor.id) linked.push(bid);
      continue;
    }
    const sameName = vendorNameKey(bid.organization) === vendor.nameKey;
    const sameDomain = !!vendor.domain && emailDomain(bid.email) === vendor.domain;
    if (sameName || sameDomain) named.push(bid);
    else if (shortlistedBundleIds.has(bid.bundleId)) shortlisted.push(bid);
  }
  return [...linked, ...named, ...shortlisted];
}
